import {graphql, readInlineData, useFragment, useRelayEnvironment} from 'react-relay'
import {useCallback, useState} from 'react'
import {useToastContext} from '@github-ui/toast'
import {ActionList, Box, Button, Dialog} from '@primer/react'
import type {IssueTypesDialogIssueTypes$key} from './__generated__/IssueTypesDialogIssueTypes.graphql'
import type {
  IssueTypesDialogIssueType$data,
  IssueTypesDialogIssueType$key,
} from './__generated__/IssueTypesDialogIssueType.graphql'
import {commitUpdateIssueIssueTypeMutation} from '../../mutations/update-issue-issue-type-mutation'
import {LABELS} from '../../constants/labels'
import {ERRORS} from '../../constants/errors'
import {BUTTON_LABELS} from '../..'
import {useAnalytics} from '@github-ui/use-analytics'

const UNSET_ID = 'UNSET'
const CUSTOM_ISSUE_TYPE = 'CUSTOM'
const UNSET_ISSUE_TYPE = {
  id: UNSET_ID,
  name: LABELS.issueTypes.noIssueTypeOptionName,
  isEnabled: true,
  issueType: CUSTOM_ISSUE_TYPE,
  description: null,
  ' $fragmentType': 'IssueTypesDialogIssueType',
} as IssueTypesDialogIssueType$data

export const IssueTypeFragment = graphql`
  fragment IssueTypesDialogIssueType on IssueType @inline {
    id
    # eslint-disable-next-line relay/unused-fields
    issueType
    name
    isEnabled
    description
  }
`

export const IssueTypesFragment = graphql`
  fragment IssueTypesDialogIssueTypes on IssueTypeConnection {
    edges {
      node {
        ...IssueTypesDialogIssueType
      }
    }
  }
`

export type IssueTypesDialogProps = {
  onClose: () => void
  isOpen: boolean
  issueId: string
  issueTypes: IssueTypesDialogIssueTypes$key | null
  initialSelectedType: IssueTypesDialogIssueType$key | null
  returnFocusRef?: React.RefObject<HTMLButtonElement>
}

export const IssueTypesDialog = ({
  onClose,
  issueId,
  issueTypes,
  initialSelectedType,
  isOpen,
  returnFocusRef,
}: IssueTypesDialogProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const {sendAnalyticsEvent} = useAnalytics()

  const preloadedIssueTypes = useFragment<IssueTypesDialogIssueTypes$key>(IssueTypesFragment, issueTypes)
  // eslint-disable-next-line no-restricted-syntax
  const selectedIssueType = readInlineData<IssueTypesDialogIssueType$key>(IssueTypeFragment, initialSelectedType)

  const enabledIssueTypes = (preloadedIssueTypes?.edges || []).flatMap(issueType => {
    if (!issueType?.node) return []

    // eslint-disable-next-line no-restricted-syntax
    const issueTypeNode = readInlineData<IssueTypesDialogIssueType$key>(IssueTypeFragment, issueType.node)
    return issueTypeNode.isEnabled ? [issueTypeNode] : []
  })

  // Adding the option of unsetting the issue type to the array of the returned issue types which are enabled for this repo
  enabledIssueTypes.unshift(UNSET_ISSUE_TYPE)

  const [selectedType, setSelectedType] = useState<IssueTypesDialogIssueType$data | null>(
    selectedIssueType || UNSET_ISSUE_TYPE,
  )

  const handleSelect = useCallback(() => {
    const issueTypeId = selectedType?.id === UNSET_ID ? null : selectedType?.id
    sendAnalyticsEvent('issue_viewer.update_issue_type', 'ISSUE_VIEWER_UPDATE_ISSUE_TYPE_DIALOG', {
      initialIssueTypeId: selectedIssueType?.id ?? '',
      issueTypeId: issueTypeId ?? '',
    })

    commitUpdateIssueIssueTypeMutation({
      environment,
      input: {issueId, issueTypeId},
      onError: (e: Error) => {
        onClose()
        // eslint-disable-next-line compat/compat
        reportError(formatError(e.message))
        addToast({type: 'error', message: ERRORS.issueTypeUpdateError})
      },
      onCompleted: onClose,
    })
  }, [addToast, environment, issueId, onClose, selectedType, selectedIssueType, sendAnalyticsEvent])

  return (
    <Dialog
      title={LABELS.issueTypes.selectIssueType}
      onDismiss={onClose}
      isOpen={isOpen}
      sx={{width: '457px'}}
      returnFocusRef={returnFocusRef}
    >
      <Dialog.Header>{LABELS.issueTypes.issueTypeDialogHeader}</Dialog.Header>
      <ActionList
        selectionVariant="single"
        role="menu"
        aria-label="Issue Type selection"
        sx={{maxHeight: '300px', overflowY: 'scroll'}}
      >
        {enabledIssueTypes.map((issueType, index) => (
          <ActionList.Item
            key={index}
            role="menuitemradio"
            selected={issueType.id === selectedType?.id}
            aria-checked={issueType.id === selectedType?.id}
            onSelect={() => setSelectedType(issueType)}
          >
            {issueType.name}
            <ActionList.Description variant="block">{issueType.description}</ActionList.Description>
          </ActionList.Item>
        ))}
      </ActionList>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          borderTopColor: 'border.subtle',
          borderTopStyle: 'solid',
          borderTopWidth: '1px',
          py: 2,
          pr: 2,
        }}
      >
        <Button sx={{mr: 2}} onClick={onClose}>
          {BUTTON_LABELS.cancel}
        </Button>
        <Button variant="primary" onClick={handleSelect}>
          {BUTTON_LABELS.changeType}
        </Button>
      </Box>
    </Dialog>
  )
}

const formatError = (message: string) => new Error(ERRORS.issueTypeUpdateErrorReporting(message))

try{ IssueTypesDialog.displayName ||= 'IssueTypesDialog' } catch {}